import React, { useMemo } from 'react';
import { Edit } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Divider, Typography } from '@mui/material';

import { useBundleCatalog } from '../store/hooks';
import type { BillingInterval, BundleWithStatus } from '../types';

import { BillingIntervalToggle } from './BillingIntervalToggle';

interface PlanSummaryProps {
  selectedTier: BundleWithStatus | null;
  selectedAddOns: BundleWithStatus[];
  billingInterval: BillingInterval;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  onChangeTier?: () => void;
  onContinue?: () => void;
  showContinueButton?: boolean;
}

export const PlanSummary: React.FC<PlanSummaryProps> = ({
  selectedTier,
  selectedAddOns,
  billingInterval,
  onBillingIntervalChange,
  onChangeTier,
  onContinue,
  showContinueButton = true,
}) => {
  const { annualDiscountRange } = useBundleCatalog();

  // Helper to get display price based on billing interval
  // Note: Math.round() is intentional for display purposes only when isMonthlyEquivalent is true.
  // Actual billing uses exact yearly_unit_amount, not the divided value.
  const getDisplayPrice = (item: BundleWithStatus, isMonthlyEquivalent: boolean = true): number => {
    if (billingInterval === 'month') return item.monthly_unit_amount;
    return isMonthlyEquivalent ? Math.round(item.yearly_unit_amount / 12) : item.yearly_unit_amount;
  };

  // Derive currency from selected tier, fallback to USD
  const currency = selectedTier?.currency || 'USD';

  const formatCurrency = (amountInCents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amountInCents / 100);
  };

  // Calculate monthly equivalent (for display/comparison)
  const monthlyEquivalent = useMemo(() => {
    let total = 0;
    if (selectedTier) {
      total += getDisplayPrice(selectedTier, true);
    }
    selectedAddOns.forEach(addOn => {
      total += getDisplayPrice(addOn, true);
    });
    return total;
  }, [selectedTier, selectedAddOns, billingInterval]);

  // Calculate actual annual total (not divided by 12) - the amount due today for annual billing
  const annualTotal = useMemo(() => {
    let total = 0;
    if (selectedTier) {
      total += getDisplayPrice(selectedTier, false);
    }
    selectedAddOns.forEach(addOn => {
      total += getDisplayPrice(addOn, false);
    });
    return total;
  }, [selectedTier, selectedAddOns, billingInterval]);

  // Calculate savings vs monthly billing (only for annual)
  const savings = useMemo(() => {
    if (billingInterval === 'month' || !selectedTier) {
      return null;
    }

    let monthlyTotal = 0;
    let annualTotal = 0;

    if (selectedTier) {
      monthlyTotal += selectedTier.monthly_unit_amount * 12;
      annualTotal += selectedTier.yearly_unit_amount;
    }

    selectedAddOns.forEach(addOn => {
      monthlyTotal += addOn.monthly_unit_amount * 12;
      annualTotal += addOn.yearly_unit_amount;
    });

    const calculatedSavings = monthlyTotal - annualTotal;
    return calculatedSavings > 0 ? calculatedSavings : null;
  }, [selectedTier, selectedAddOns, billingInterval]);

  return (
    <Card
      sx={{
        position: 'relative',
        height: 'fit-content',
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        minHeight: '100%',
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Your Plan
          </Typography>
          {onChangeTier && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={onChangeTier}
              startIcon={<Edit />}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              Edit
            </Button>
          )}
        </Box>

        {/* Itemized Bundle List */}
        <Box sx={{ mb: 3 }}>
          {selectedTier ? (
            <>
              {/* Tier line item */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedTier.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Base tier
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(
                    billingInterval === 'month'
                      ? selectedTier.monthly_unit_amount
                      : Math.round(selectedTier.yearly_unit_amount / 12),
                  )}
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: 'text.secondary', ml: 0.5 }}
                  >
                    /mo
                  </Typography>
                </Typography>
              </Box>

              {/* Add-ons section */}
              {selectedAddOns.length > 0 ? (
                selectedAddOns.map(addOn => (
                  <Box
                    key={addOn.bundle_id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">{addOn.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(
                        billingInterval === 'month'
                          ? addOn.monthly_unit_amount
                          : Math.round(addOn.yearly_unit_amount / 12),
                      )}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: 'text.secondary', ml: 0.5 }}
                      >
                        /mo
                      </Typography>
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{ py: 2, textAlign: 'center', backgroundColor: 'grey.50', borderRadius: 1 }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No add-ons selected
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No tier selected
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Monthly Rate with Toggle (side by side) */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              {billingInterval === 'year' ? 'Monthly Rate' : 'Total'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatCurrency(monthlyEquivalent)}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'text.secondary', ml: 0.5 }}
              >
                /mo
              </Typography>
            </Typography>
          </Box>
          <BillingIntervalToggle
            value={billingInterval}
            onChange={onBillingIntervalChange}
            size="small"
            discountRange={annualDiscountRange}
          />
        </Box>

        {/* Due Today - Full Width (only for annual billing) */}
        {billingInterval === 'year' && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                Due Today
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(annualTotal)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                Billed annually
              </Typography>
              {savings !== null && savings > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    bgcolor: 'info.light',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'white' }}>
                    Save {formatCurrency(savings)}/year vs monthly
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Monthly billing caption */}
        {billingInterval === 'month' && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 3, display: 'block' }}>
            Billed monthly
          </Typography>
        )}

        {/* Continue Button */}
        {showContinueButton && onContinue && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={onContinue}
            disabled={!selectedTier}
            sx={{
              mt: 'auto',
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Continue to Pay
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
